import type { CashSummary } from '../../cash/domain-api';
import type { SalesByCashierRow, SalesByTimeBandRow, SalesComparison, SalesSnapshot } from '../../sales/public-api';
import type { IntelligenceKpisResponse } from '../domain/reportsIntelligenceTypes';
import type {
  BranchCashSessionReportRow,
  PosExceptionSummary,
  ReportsOperacionKpiSources,
  SalesDailyRow,
} from '../domain/reportsTypes';
import type { ReportsQueryScope } from '../domain/reportsQueryScope';
import {
  createLocalReaders,
  fetchOnlineDashboard,
  fetchOfflineDashboard,
} from './reportsOperacionDashboardBlocks';
import type {
  LoadReportsOperacionDashboardParams,
  ReportsOperacionDashboardDeps,
} from './reportsOperacionDashboardTypes';
export type {
  LoadReportsOperacionDashboardParams,
  ReportsOperacionDashboardDeps,
} from './reportsOperacionDashboardTypes';

export type LoadReportsOperacionDashboardResult = {
  cashData: CashSummary;
  todaySalesData: SalesSnapshot;
  periodSalesData: SalesSnapshot;
  previousPeriodSalesData: SalesSnapshot | null;
  comparison: SalesComparison;
  bands: SalesByTimeBandRow[];
  byCashier: SalesByCashierRow[];
  dailySeries: SalesDailyRow[] | null;
  kpiSources: ReportsOperacionKpiSources;
  branchCashSessions: BranchCashSessionReportRow[];
  posExceptionSummary: PosExceptionSummary | null;
  periodRentabilidadKpis: IntelligenceKpisResponse | null;
  previousPeriodRentabilidadKpis: IntelligenceKpisResponse | null;
  salesByCashierSource: 'server' | 'device';
};

/**
 * Núcleo del caso de uso (prioridad remota + fallback local). Recibe dependencias explícitas.
 */
export async function loadReportsOperacionDashboardCore(
  params: LoadReportsOperacionDashboardParams,
  deps: ReportsOperacionDashboardDeps
): Promise<LoadReportsOperacionDashboardResult> {
  const { branchId, reportsQueryScope, dateForBandsIsoResolved, online } = params;

  const readers = createLocalReaders(branchId, reportsQueryScope, dateForBandsIsoResolved, deps);

  if (online) {
    const result = await fetchOnlineDashboard(params, deps, readers);
    return {
      ...result,
      periodRentabilidadKpis: null,
      previousPeriodRentabilidadKpis: null,
    };
  } else {
    const result = await fetchOfflineDashboard(params, deps, readers);
    return {
      ...result,
      periodRentabilidadKpis: null,
      previousPeriodRentabilidadKpis: null,
    };
  }
}

export type LoadReportsOperacionRentabilidadParams = {
  branchId: string;
  fromIso: string;
  toIso: string;
  prevPeriodRange: { fromIso: string; toIso: string } | null;
  reportsQueryScope: ReportsQueryScope;
  online: boolean;
};

export type LoadReportsOperacionRentabilidadResult = {
  periodRentabilidadKpis: IntelligenceKpisResponse | null;
  previousPeriodRentabilidadKpis: IntelligenceKpisResponse | null;
};

/**
 * Rentabilidad / intelligence KPIs — lazy (no bloquea carga del dashboard operación).
 * El error del período actual propaga: la UI muestra estado de error (no $0 falsos).
 * El período anterior es secundario (solo alimenta deltas): su fallo devuelve null.
 */
export async function loadReportsOperacionRentabilidadKpis(
  params: LoadReportsOperacionRentabilidadParams,
  deps: Pick<ReportsOperacionDashboardDeps, 'getIntelligenceKpis'>
): Promise<LoadReportsOperacionRentabilidadResult> {
  if (!params.online) {
    return { periodRentabilidadKpis: null, previousPeriodRentabilidadKpis: null };
  }

  const rentPeriodP = deps.getIntelligenceKpis({
    branchId: params.branchId,
    fromIso: params.fromIso,
    toIso: params.toIso,
    queryScope: params.reportsQueryScope,
  });
  const rentPrevP =
    params.prevPeriodRange != null
      ? deps
          .getIntelligenceKpis({
            branchId: params.branchId,
            fromIso: params.prevPeriodRange.fromIso,
            toIso: params.prevPeriodRange.toIso,
            queryScope: params.reportsQueryScope,
          })
          .catch((e) => {
            console.warn('[Reports] rentabilidad período anterior', e);
            return null;
          })
      : Promise.resolve(null);

  const [periodRentabilidadKpis, previousPeriodRentabilidadKpis] = await Promise.all([
    rentPeriodP,
    rentPrevP,
  ]);

  return { periodRentabilidadKpis, previousPeriodRentabilidadKpis };
}
